import React, { useState, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Rating from '@mui/material/Rating';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import StarIcon from '@mui/icons-material/Star';
import ReCAPTCHA from 'react-google-recaptcha';
import * as api from '../../api';

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
}

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

const ratingLabels: { [key: number]: string } = {
  1: 'Poor',
  2: 'Fair',
  3: 'Good',
  4: 'Very Good',
  5: 'Excellent',
};

function FeedbackDialog({ open, onClose }: FeedbackDialogProps) {
  const [rating, setRating] = useState<number | null>(null);
  const [hoverRating, setHoverRating] = useState<number>(-1);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [category, setCategory] = useState<'bug' | 'feature' | 'general' | ''>('');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const handleClose = () => {
    setRating(null);
    setMessage('');
    setEmail('');
    setCategory('');
    setCaptchaToken(null);
    setError(null);
    setLoading(false);
    setSuccess(false);
    recaptchaRef.current?.reset();
    onClose();
  };

  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!rating) {
      setError('Please provide a rating');
      return;
    }

    if (!message.trim()) {
      setError('Please provide feedback message');
      return;
    }

    if (!captchaToken) {
      setError('Please complete the captcha verification');
      return;
    }

    setLoading(true);

    try {
      await api.submitFeedback({
        rating,
        message: message.trim(),
        email: email.trim() || undefined,
        category: category || undefined,
        captcha_token: captchaToken,
      });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit feedback. Please try again.');
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="span" fontWeight={600}>
          {success ? 'Thank You!' : 'Share Your Feedback'}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ color: 'grey.500' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 3, py: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              Feedback Submitted!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              We appreciate you taking the time to share your thoughts with us.
              Your feedback helps us improve.
            </Typography>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            {/* Rating */}
            <Box sx={{ mb: 3 }}>
              <Typography component="legend" sx={{ mb: 1, fontWeight: 500 }}>
                How would you rate your experience? *
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Rating
                  name="feedback-rating"
                  value={rating}
                  onChange={(_, newValue) => setRating(newValue)}
                  onChangeActive={(_, newHover) => setHoverRating(newHover)}
                  size="large"
                  emptyIcon={<StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
                />
                {rating !== null && (
                  <Typography variant="body2" color="text.secondary">
                    {ratingLabels[hoverRating !== -1 ? hoverRating : rating]}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Category */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="feedback-category-label">Category (Optional)</InputLabel>
              <Select
                labelId="feedback-category-label"
                value={category}
                label="Category (Optional)"
                onChange={(e) => setCategory(e.target.value as typeof category)}
                disabled={loading}
              >
                <MenuItem value="">
                  <em>Select a category</em>
                </MenuItem>
                <MenuItem value="general">General Feedback</MenuItem>
                <MenuItem value="feature">Feature Request</MenuItem>
                <MenuItem value="bug">Bug Report</MenuItem>
              </Select>
            </FormControl>

            {/* Message */}
            <TextField
              fullWidth
              label="Your Feedback *"
              multiline
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us what you think... What do you like? What could be improved?"
              disabled={loading}
              sx={{ mb: 3 }}
            />

            {/* Email */}
            <TextField
              fullWidth
              label="Email (Optional)"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="If you'd like us to follow up with you"
              helperText="We'll only use this to respond to your feedback"
              disabled={loading}
              sx={{ mb: 3 }}
            />

            {/* reCAPTCHA */}
            {RECAPTCHA_SITE_KEY ? (
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={handleCaptchaChange}
                />
              </Box>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                reCAPTCHA is not configured. Please set REACT_APP_RECAPTCHA_SITE_KEY.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {success ? (
          <Button onClick={handleClose} variant="contained" color="primary">
            Close
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={loading || !rating || !message.trim() || !captchaToken}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit Feedback'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default FeedbackDialog;
